import React, { useState, useEffect, useCallback } from 'react';
import { MegaphoneIcon, XMarkIcon } from '@heroicons/react/24/outline';
import apiClient from '../utils/apiClient';

const BroadcastModal = () => {
  const [broadcasts, setBroadcasts] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);

  const fetchPending = useCallback(async () => {
    try {
      const data = await apiClient.get('/api/auth/broadcasts/pending/');
      if (Array.isArray(data) && data.length > 0) {
        setBroadcasts(data);
        setCurrentIndex(0);
      }
    } catch (err) {
      // Silently ignore - don't disrupt user experience
    }
  }, []);

  useEffect(() => {
    // Small delay so it doesn't flash immediately on page load
    const timer = setTimeout(fetchPending, 1500);
    return () => clearTimeout(timer);
  }, [fetchPending]);

  const dismiss = async () => {
    const current = broadcasts[currentIndex];
    if (!current) return;

    try {
      await apiClient.post(`/api/auth/broadcasts/${current.id}/dismiss/`);
    } catch (err) {
      // Ignore dismiss errors
    }

    if (currentIndex < broadcasts.length - 1) {
      setCurrentIndex(prev => prev + 1);
    } else {
      setBroadcasts([]);
    }
  };

  if (broadcasts.length === 0) return null;

  const current = broadcasts[currentIndex];
  if (!current) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={dismiss} />

      {/* Modal */}
      <div className="relative w-full max-w-lg bg-white dark:bg-gray-800 rounded-2xl shadow-2xl overflow-hidden animate-in">
        {/* Header bar */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MegaphoneIcon className="h-5 w-5 text-white" />
            <span className="text-white text-xs font-semibold uppercase tracking-wider">Broadcast</span>
            {broadcasts.length > 1 && (
              <span className="text-blue-200 text-xs">({currentIndex + 1}/{broadcasts.length})</span>
            )}
          </div>
          <button onClick={dismiss} className="text-white/70 hover:text-white transition-colors">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-5">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-3">{current.title}</h2>
          
          {/* Image */}
          {current.image && (
            <div className="mb-4 rounded-lg overflow-hidden">
              <img 
                src={current.image} 
                alt={current.title}
                className="w-full h-auto object-cover max-h-64"
              />
            </div>
          )}
          
          <div className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed max-h-[50vh] overflow-y-auto">
            {renderFormattedText(current.message)}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 pb-5 flex items-center justify-between">
          <span className="text-xs text-gray-400">
            {new Date(current.created_at).toLocaleDateString()}
          </span>
          <button
            onClick={dismiss}
            className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium transition-colors"
          >
            {currentIndex < broadcasts.length - 1 ? 'Next' : 'Got it'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Helper function to render formatted text
const renderFormattedText = (text) => {
  if (!text) return null;
  
  // Split by lines to handle formatting
  const lines = text.split('\n');
  
  return lines.map((line, index) => {
    // Process bold and italic
    const parts = [];
    let lastIndex = 0;
    
    // Match **bold** and *italic*
    const regex = /(\*\*[^*]+\*\*|\*[^*]+\*)/g;
    let match;
    
    while ((match = regex.exec(line)) !== null) {
      // Add text before match
      if (match.index > lastIndex) {
        parts.push(line.substring(lastIndex, match.index));
      }
      
      const matched = match[0];
      if (matched.startsWith('**') && matched.endsWith('**')) {
        // Bold
        parts.push(<strong key={`${index}-${match.index}`}>{matched.slice(2, -2)}</strong>);
      } else if (matched.startsWith('*') && matched.endsWith('*')) {
        // Italic
        parts.push(<em key={`${index}-${match.index}`}>{matched.slice(1, -1)}</em>);
      }
      
      lastIndex = match.index + matched.length;
    }
    
    // Add remaining text
    if (lastIndex < line.length) {
      parts.push(line.substring(lastIndex));
    }
    
    // Return formatted line
    return (
      <div key={index}>
        {parts.length > 0 ? parts : line}
      </div>
    );
  });
};

export default BroadcastModal;
